import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEventHandler,
  type Ref,
} from 'react';
import { CalendarIcon, ChevronRightIcon, CloseIcon } from '@/components/icons';
import { Button } from './Button';
import { TextField } from './TextField';
import { FIELD_LABEL, FIELD_STACK, fieldShell } from './field-shell';
import {
  calendarDays,
  dateKey,
  formatDateKey,
  parseDateKey,
  shiftDays,
  shiftMonth,
  type DateRange,
} from './date-picker-utils';
import './DatePicker.css';

interface CommonProps {
  label: string;
  disabled?: boolean | undefined;
  min?: string | undefined;
  max?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  className?: string | undefined;
}
type DatePickerProps = CommonProps &
  (
    | {
        mode?: 'single';
        value: string;
        onValueChange: (value: string) => void;
        name?: string;
        required?: boolean | undefined;
        onBlur?: FocusEventHandler<HTMLInputElement>;
      }
    | {
        /** flexible autorise le choix journée/plage dans le calendrier. */
        mode: 'range' | 'flexible';
        value: DateRange;
        onValueChange: (value: DateRange) => void;
      }
  );

const isSinglePicker = (
  props: DatePickerProps,
): props is Extract<DatePickerProps, { mode?: 'single' }> =>
  props.mode !== 'range' && props.mode !== 'flexible';

const MONTHS = Array.from({ length: 12 }, (_, index) =>
  new Date(2024, index, 1).toLocaleDateString('fr-FR', { month: 'long' }),
);
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const dayLabel = (date: string) =>
  parseDateKey(date)!.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export const DatePicker = ({
  ref: inputRef,
  ...props
}: DatePickerProps & { ref?: Ref<HTMLInputElement> | undefined }) => {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const focusRequested = useRef(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>({ from: '', to: '' });
  const [selectionMode, setSelectionMode] = useState<'single' | 'range'>('range');
  const [choosingEnd, setChoosingEnd] = useState(false);
  const [hovered, setHovered] = useState('');
  const today = dateKey(new Date());
  const [month, setMonth] = useState(today.slice(0, 7));
  const [focused, setFocused] = useState(today);
  const min = parseDateKey(props.min ?? '') ? props.min : undefined;
  const max = parseDateKey(props.max ?? '') ? props.max : undefined;
  const inBounds = (day: string) => (!min || day >= min) && (!max || day <= max);
  const close = (restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  };
  const rangeMode = !isSinglePicker(props);
  const selectedRange: DateRange = isSinglePicker(props)
    ? { from: props.value, to: props.value }
    : props.value;
  const summary =
    selectedRange.from && selectedRange.to
      ? selectedRange.from === selectedRange.to
        ? formatDateKey(selectedRange.from)
        : `${formatDateKey(selectedRange.from)} → ${formatDateKey(selectedRange.to)}`
      : selectedRange.from
        ? `Depuis le ${formatDateKey(selectedRange.from)}`
        : selectedRange.to
          ? `Jusqu’au ${formatDateKey(selectedRange.to)}`
          : 'Choisir une date ou une plage';
  const show = () => {
    if (open) {
      close();
      return;
    }
    if (props.disabled || trigger.current?.matches(':disabled')) return;
    const next = { ...selectedRange };
    const candidate = parseDateKey(next.from) ? next.from : next.to;
    let day = parseDateKey(candidate) ? candidate : today;
    if (min && day < min) day = min;
    if (max && day > max) day = max;
    setDraft(next);
    setFocused(day);
    setMonth(day.slice(0, 7));
    setChoosingEnd(false);
    setHovered('');
    setSelectionMode(
      !rangeMode || (props.mode === 'flexible' && Boolean(next.from) && next.from === next.to)
        ? 'single'
        : 'range',
    );
    setOpen(true);
  };

  useLayoutEffect(() => {
    if (!open) return;
    const element = panel.current;
    if (element?.showPopover) element.showPopover();
    else if (element) element.style.display = 'block';
    const position = () => {
      const rect = anchor.current?.getBoundingClientRect();
      if (!rect || !element) return;
      const width = element.offsetWidth;
      const height = element.offsetHeight;
      // Les rectangles sont en pixels écran ; les styles fixed utilisent les
      // pixels de mise en page, avant le zoom global de l'interface.
      const scale = Number.parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
      const viewport = window.visualViewport;
      const top = (viewport?.offsetTop ?? 0) / scale;
      const left = (viewport?.offsetLeft ?? 0) / scale;
      const bottom = top + (viewport?.height ?? window.innerHeight) / scale;
      const right = left + (viewport?.width ?? window.innerWidth) / scale;
      const anchorBottom = rect.bottom / scale;
      element.style.left = `${Math.max(left + 8, Math.min(rect.left / scale, right - width - 8))}px`;
      element.style.top = `${Math.max(top + 8, anchorBottom + height + 8 <= bottom ? anchorBottom + 6 : rect.top / scale - height - 6)}px`;
    };
    position();
    panel.current
      ?.querySelector<HTMLButtonElement>('[data-date][tabindex="0"]')
      ?.focus({ preventScroll: true });
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    window.visualViewport?.addEventListener('resize', position);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(position) : null;
    if (element) observer?.observe(element);
    return () => {
      if (element?.isConnected) element.hidePopover?.();
      observer?.disconnect();
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      window.visualViewport?.removeEventListener('resize', position);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', outside);
    };
  }, [open]);
  useLayoutEffect(() => {
    if (
      open &&
      (focusRequested.current ||
        (panel.current?.contains(document.activeElement) &&
          document.activeElement?.hasAttribute('data-date')))
    )
      panel.current
        ?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)
        ?.focus({ preventScroll: true });
    focusRequested.current = false;
  }, [focused, month, open]);

  const choose = (day: string) => {
    if (!inBounds(day)) return;
    if (isSinglePicker(props)) {
      props.onValueChange(day);
      close();
      return;
    }
    if (selectionMode === 'single') {
      setDraft({ from: day, to: day });
      return;
    }
    if (!choosingEnd || !draft.from) {
      setDraft({ from: day, to: '' });
      setChoosingEnd(true);
    } else {
      setDraft({
        from: day < draft.from ? day : draft.from,
        to: day < draft.from ? draft.from : day,
      });
      setChoosingEnd(false);
    }
    setHovered('');
  };
  const rangeError =
    draft.from && draft.to && draft.from > draft.to
      ? 'La fin doit être postérieure ou égale au début.'
      : [draft.from, draft.to].some((date) => date && (!parseDateKey(date) || !inBounds(date)))
        ? 'Choisissez une date dans les limites autorisées.'
        : null;
  const year = Number(month.slice(0, 4));
  const years = Array.from(
    new Set([
      year,
      ...Array.from({ length: 201 }, (_, index) => new Date().getFullYear() - 100 + index),
    ]),
  ).sort((a, b) => a - b);
  const days = calendarDays(month);
  const end = choosingEnd && hovered ? hovered : draft.to;
  const startHighlight = draft.from && end ? (draft.from < end ? draft.from : end) : draft.from;
  const endHighlight = draft.from && end ? (draft.from < end ? end : draft.from) : draft.from;
  const viewMonth = (nextMonth: string) => {
    let first = `${nextMonth}-01`;
    if (min && first < min) first = min;
    if (max && first > max) first = max;
    setMonth(first.slice(0, 7));
    setFocused(first);
  };
  const goMonth = (offset: number) => viewMonth(shiftMonth(`${month}-01`, offset).slice(0, 7));
  const messageId = `${id}-message`;
  return (
    <div
      className="date-picker"
      ref={root}
      onKeyDown={(event) => {
        if (open && event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
      }}
    >
      <div ref={anchor}>
        {!isSinglePicker(props) ? (
          <div className={FIELD_STACK}>
            <label id={`${id}-label`} htmlFor={`${id}-trigger`} className={FIELD_LABEL}>
              {props.label}
            </label>
            <div
              className={fieldShell({
                hasError: Boolean(props.error),
                disabled: props.disabled ?? false,
              })}
            >
              <button
                id={`${id}-trigger`}
                type="button"
                ref={trigger}
                className={`date-picker__trigger ${props.className ?? ''}`}
                disabled={props.disabled}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls={open ? id : undefined}
                aria-labelledby={`${id}-label ${id}-value`}
                aria-describedby={props.error || props.hint ? messageId : undefined}
                aria-invalid={Boolean(props.error)}
                onClick={show}
              >
                <span id={`${id}-value`} data-placeholder={!props.value.from && !props.value.to}>
                  {summary}
                </span>
                <CalendarIcon aria-hidden />
              </button>
            </div>
            {props.error || props.hint ? (
              <p
                id={messageId}
                role={props.error ? 'alert' : undefined}
                className={props.error ? 'text-xs text-red-600' : 'text-xs text-ink-500'}
              >
                {props.error ?? props.hint}
              </p>
            ) : null}
          </div>
        ) : (
          <TextField
            label={props.label}
            type="date"
            value={props.value}
            onChange={(event) => props.onValueChange(event.target.value)}
            {...(props.onBlur ? { onBlur: props.onBlur } : {})}
            {...(props.name ? { name: props.name } : {})}
            ref={inputRef}
            min={min}
            max={max}
            required={props.required}
            disabled={props.disabled}
            error={props.error}
            {...(props.hint ? { hint: props.hint } : {})}
            className={`date-picker__input ${props.className ?? ''}`}
            trailing={
              <button
                type="button"
                ref={trigger}
                className="date-picker__calendar-button"
                aria-label={`Ouvrir le calendrier : ${props.label}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls={open ? id : undefined}
                disabled={props.disabled}
                onClick={show}
              >
                <CalendarIcon />
              </button>
            }
          />
        )}
      </div>
      {open ? (
        <div
          id={id}
          ref={panel}
          popover="manual"
          className="date-picker__panel"
          role="dialog"
          aria-label={`Calendrier : ${props.label}`}
        >
          <div className="date-picker__top">
            <strong>{props.label}</strong>
            <button type="button" aria-label="Fermer le calendrier" onClick={() => close()}>
              <CloseIcon />
            </button>
          </div>
          {props.mode === 'flexible' ? (
            <div className="date-picker__modes" aria-label="Mode de sélection">
              <button
                type="button"
                aria-pressed={selectionMode === 'single'}
                onClick={() => {
                  setSelectionMode('single');
                  setDraft({ from: draft.from || draft.to, to: draft.from || draft.to });
                  setChoosingEnd(false);
                }}
              >
                Une journée
              </button>
              <button
                type="button"
                aria-pressed={selectionMode === 'range'}
                onClick={() => {
                  setSelectionMode('range');
                  setChoosingEnd(false);
                }}
              >
                Une plage
              </button>
            </div>
          ) : null}
          {rangeMode ? (
            <div className="date-picker__dates">
              <TextField
                label={selectionMode === 'single' ? 'Journée' : 'Depuis le'}
                type="date"
                value={draft.from}
                min={min}
                max={max}
                onChange={(event) => {
                  const from = event.target.value;
                  setDraft({ ...draft, from, ...(selectionMode === 'single' ? { to: from } : {}) });
                  setChoosingEnd(false);
                  if (from) {
                    setMonth(from.slice(0, 7));
                    setFocused(from);
                  }
                }}
              />
              {selectionMode === 'range' ? (
                <TextField
                  label="Jusqu’au"
                  type="date"
                  value={draft.to}
                  min={draft.from || min}
                  max={max}
                  onChange={(event) => {
                    setDraft({ ...draft, to: event.target.value });
                    setChoosingEnd(false);
                  }}
                />
              ) : null}
            </div>
          ) : null}
          <div className="date-picker__navigation">
            <button
              type="button"
              aria-label="Mois précédent"
              disabled={Boolean(min && month <= min.slice(0, 7))}
              onClick={() => goMonth(-1)}
            >
              <ChevronRightIcon className="rotate-180" />
            </button>
            <select
              aria-label="Mois du calendrier"
              value={Number(month.slice(5)) - 1}
              onChange={(event) => {
                const next = `${year}-${String(Number(event.target.value) + 1).padStart(2, '0')}`;
                viewMonth(next);
              }}
            >
              {MONTHS.map((label, index) => (
                <option value={index} key={label}>
                  {label}
                </option>
              ))}
            </select>
            <select
              aria-label="Année du calendrier"
              value={year}
              onChange={(event) => {
                const next = `${event.target.value}-${month.slice(5)}`;
                viewMonth(next);
              }}
            >
              {years.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <button
              type="button"
              aria-label="Mois suivant"
              disabled={Boolean(max && month >= max.slice(0, 7))}
              onClick={() => goMonth(1)}
            >
              <ChevronRightIcon />
            </button>
          </div>
          <div
            className="date-picker__grid"
            role="grid"
            aria-label={parseDateKey(`${month}-01`)!.toLocaleDateString('fr-FR', {
              month: 'long',
              year: 'numeric',
            })}
          >
            <div role="row" className="date-picker__weekdays">
              {WEEKDAYS.map((day) => (
                <span role="columnheader" key={day}>
                  {day}
                </span>
              ))}
            </div>
            {Array.from({ length: 6 }, (_, week) => (
              <div role="row" key={week}>
                {days.slice(week * 7, week * 7 + 7).map((day) => {
                  const selected = Boolean(
                    draft.from && day >= startHighlight && day <= endHighlight,
                  );
                  return (
                    <span role="gridcell" aria-selected={selected} key={day}>
                      <button
                        type="button"
                        data-date={day}
                        data-outside={day.slice(0, 7) !== month}
                        data-selected={selected}
                        data-endpoint={day === draft.from || day === draft.to}
                        aria-current={day === today ? 'date' : undefined}
                        aria-label={dayLabel(day)}
                        disabled={!inBounds(day)}
                        tabIndex={day === focused ? 0 : -1}
                        onFocus={() => setFocused(day)}
                        onMouseEnter={() => setHovered(day)}
                        onClick={() => choose(day)}
                        onKeyDown={(event) => {
                          const offsets: Record<string, number> = {
                            ArrowLeft: -1,
                            ArrowRight: 1,
                            ArrowUp: -7,
                            ArrowDown: 7,
                          };
                          let next: string | undefined;
                          if (event.key in offsets) next = shiftDays(day, offsets[event.key]!);
                          else if (event.key === 'Home')
                            next = shiftDays(day, -((parseDateKey(day)!.getDay() + 6) % 7));
                          else if (event.key === 'End')
                            next = shiftDays(day, 6 - ((parseDateKey(day)!.getDay() + 6) % 7));
                          else if (event.key === 'PageUp' || event.key === 'PageDown')
                            next = shiftMonth(
                              day,
                              (event.key === 'PageUp' ? -1 : 1) * (event.shiftKey ? 12 : 1),
                            );
                          if (next) {
                            event.preventDefault();
                            if (min && next < min) next = min;
                            if (max && next > max) next = max;
                            focusRequested.current = true;
                            setFocused(next);
                            setMonth(next.slice(0, 7));
                          }
                        }}
                      >
                        {Number(day.slice(8))}
                      </button>
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
          {rangeMode ? (
            <p className="date-picker__instruction" aria-live="polite">
              {rangeError ??
                (selectionMode === 'single'
                  ? 'Choisissez la journée à rechercher.'
                  : choosingEnd
                    ? 'Choisissez la date de fin.'
                    : 'Choisissez un début et une fin, ou laissez une borne vide.')}
            </p>
          ) : null}
          <div className="date-picker__footer">
            <button
              type="button"
              onClick={() => {
                if (!isSinglePicker(props)) {
                  setDraft({ from: '', to: '' });
                  setChoosingEnd(false);
                } else {
                  props.onValueChange('');
                  close();
                }
              }}
            >
              Effacer
            </button>
            <button
              type="button"
              disabled={!inBounds(today)}
              onClick={() => {
                if (rangeMode) {
                  setDraft({ from: today, to: today });
                  setMonth(today.slice(0, 7));
                  setFocused(today);
                  setChoosingEnd(false);
                } else choose(today);
              }}
            >
              Aujourd’hui
            </button>
            {rangeMode ? (
              <Button
                size="sm"
                disabled={Boolean(rangeError)}
                onClick={() => {
                  if (!isSinglePicker(props)) props.onValueChange(draft);
                  close();
                }}
              >
                Valider
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
